import type { CreativeTestInput } from "~/ui-rtk/api/jobsApi"
import type { CreativeTestEligibility } from "~/ui-rtk/api/types/paid-media.types"

/**
 * Options and helpers for the "Run as a Meta creative test" mode of the
 * blast picker (MD-5569). Meta's ad-level creative test: the selected
 * sources become 2–7 variant ads inside ONE existing ad set, then an ad
 * study (SPLIT_TEST_V2) holds a share of that ad set's budget for them,
 * splits it evenly with no audience overlap, and names the high performers.
 * The original ads keep running outside the test; the variants stay in the
 * ad set afterwards with their delivery history.
 *
 * Wire shape mirrors the backend `CreativeTestBlock`; the picker holds raw
 * field text and converts at launch.
 */

export const CREATIVE_TEST_MIN_VARIANTS = 2
export const CREATIVE_TEST_MAX_VARIANTS = 7
/** Meta's recommendation for the share of the ad set budget held for the test. */
export const CREATIVE_TEST_SUGGESTED_SHARE = 20
export const CREATIVE_TEST_DEFAULT_DAYS = 7
/** Meta wants a future start; a full day of margin sidesteps time-zone edges. */
const START_HOUR_LOCAL = 9
const DAY_MS = 86_400_000

export interface CreativeTestState {
  enabled: boolean
  name: string
  /** Daily-budget ad sets take an absolute daily amount; lifetime budgets a percentage. */
  budgetMode: "daily" | "lifetime"
  /** Raw field text, dollars per day. */
  dailyBudget: string
  /** Raw field text, 1–100. */
  lifetimePercentage: string
  /** Raw field text, whole days. */
  days: string
  /** YYYY-MM-DD in the browser's local zone. */
  startDate: string
}

/** Tomorrow's date as the `<input type="date">` value. */
export function defaultStartDate(now: Date = new Date()): string {
  const tomorrow = new Date(now.getTime() + DAY_MS)
  const y = tomorrow.getFullYear()
  const m = String(tomorrow.getMonth() + 1).padStart(2, "0")
  const d = String(tomorrow.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export const DEFAULT_CREATIVE_TEST: CreativeTestState = {
  enabled: false,
  name: "",
  budgetMode: "daily",
  dailyBudget: "",
  lifetimePercentage: String(CREATIVE_TEST_SUGGESTED_SHARE),
  days: String(CREATIVE_TEST_DEFAULT_DAYS),
  startDate: "",
}

function parsePositive(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const value = Number(trimmed)
  return Number.isFinite(value) && value > 0 ? value : null
}

/** Unix-second window for the study: start at 09:00 local on `startDate`, end `days` later. */
export function creativeTestWindow(
  state: Pick<CreativeTestState, "startDate" | "days">,
): { startTime: number; endTime: number } | null {
  const days = parsePositive(state.days)
  if (days === null || !Number.isInteger(days)) return null
  const [y, m, d] = state.startDate.split("-").map(Number)
  if (!y || !m || !d) return null
  const start = new Date(y, m - 1, d, START_HOUR_LOCAL, 0, 0, 0)
  if (Number.isNaN(start.getTime())) return null
  const startTime = Math.floor(start.getTime() / 1000)
  return { startTime, endTime: startTime + days * 86_400 }
}

/**
 * Dollars/day the suggested share works out to for the eligible ad set, or
 * null when the ad set has no daily budget (lifetime budgets use the share
 * directly).
 */
export function suggestedDailyBudgetDollars(
  eligibility: CreativeTestEligibility | undefined,
): number | null {
  const cents = eligibility?.suggestedDailyBudgetCents
  return cents == null ? null : Math.round(cents) / 100
}

export interface CreativeTestContext {
  sourceCount: number
  metaSelectedCount: number
  tiktokSelectedCount: number
  eligibility?: CreativeTestEligibility
  /** ISO instant the blast itself is scheduled for, when "later" is picked. */
  scheduledAt?: string | null
  now?: Date
}

/**
 * Every reason the test can't launch as configured, in the order the user
 * should fix them. Empty means launchable. Eligibility `reasons` (bid
 * strategy, delivery, an existing study) are hard blockers; its `warnings`
 * are not — Meta decides those.
 */
export function creativeTestErrors(
  state: CreativeTestState,
  ctx: CreativeTestContext,
): string[] {
  if (!state.enabled) return []
  const errors: string[] = []
  if (
    ctx.sourceCount < CREATIVE_TEST_MIN_VARIANTS ||
    ctx.sourceCount > CREATIVE_TEST_MAX_VARIANTS
  ) {
    errors.push(
      `Select ${CREATIVE_TEST_MIN_VARIANTS}–${CREATIVE_TEST_MAX_VARIANTS} sources — each becomes one variant (you have ${ctx.sourceCount}).`,
    )
  }
  if (ctx.tiktokSelectedCount > 0) {
    errors.push("Creative tests are Meta only — deselect the TikTok ad groups.")
  }
  if (ctx.metaSelectedCount !== 1) {
    errors.push(
      `Select exactly one Meta ad set — the test runs inside it (you have ${ctx.metaSelectedCount}).`,
    )
  }
  if (!state.name.trim()) errors.push("Give the test a name.")
  if (state.budgetMode === "daily") {
    if (parsePositive(state.dailyBudget) === null) {
      errors.push("Enter a daily test budget greater than 0.")
    }
  } else {
    const pct = parsePositive(state.lifetimePercentage)
    if (pct === null || pct > 100) {
      errors.push("Enter a lifetime budget share between 1 and 100.")
    }
  }
  const window = creativeTestWindow(state)
  if (!window) {
    errors.push("Pick a start date and a whole number of days.")
  } else {
    const now = ctx.now ?? new Date()
    if (window.startTime * 1000 <= now.getTime()) {
      errors.push(
        "The test must start in the future (Meta needs tomorrow or later).",
      )
    }
    if (
      ctx.scheduledAt &&
      window.startTime * 1000 <= Date.parse(ctx.scheduledAt)
    ) {
      errors.push("The test must start after the scheduled blast time.")
    }
  }
  for (const reason of ctx.eligibility?.reasons ?? []) errors.push(reason)
  return errors
}

/** The wire block for the ad-blast batch. Only valid once `creativeTestErrors` is empty. */
export function toCreativeTestInput(
  state: CreativeTestState,
): CreativeTestInput | null {
  const window = creativeTestWindow(state)
  if (!window) return null
  const budget =
    state.budgetMode === "daily"
      ? (() => {
          const dollars = parsePositive(state.dailyBudget)
          return dollars === null
            ? null
            : { dailyBudgetCents: Math.round(dollars * 100) }
        })()
      : (() => {
          const pct = parsePositive(state.lifetimePercentage)
          return pct === null ? null : { lifetimeBudgetPercentage: pct }
        })()
  if (!budget) return null
  return { name: state.name.trim(), ...budget, ...window }
}
